import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { useSalesKpis } from "@/hooks/useInvoices";
import { SectionLoadError } from "@/components/invoicing/SectionErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { formatKpiCurrency } from "@/lib/formats/currency";

interface PaidSalesCardProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}

/**
 * YTD Paid Sales (snapshot mockup 2): travel sales paid out this year from
 * closed invoices only, with pending open bookings in the footer. Uses the
 * existing sales KPIs (paidYtd / outstandingYtd) — no new calculation.
 */
export function PaidSalesCard({
  tramsAccountId,
  viewMode,
  returnDateFrom,
  returnDateTo,
}: PaidSalesCardProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useSalesKpis({
    tramsAccountId,
    viewMode,
    returnDateFrom,
    returnDateTo,
  });

  if (error) {
    return (
      <SectionLoadError
        sectionName="YTD Paid Sales"
        error={error}
        onRetry={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/invoices/sales-kpis"] });
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card p-5">
        <Skeleton className="h-4 w-36 mb-3" />
        <Skeleton className="h-10 w-44 mb-1" />
        <Skeleton className="h-3 w-28 mb-3" />
        <Skeleton className="h-6 w-full rounded-lg" />
      </div>
    );
  }

  const paidAmount = parseFloat(data?.paidYtd ?? "0") || 0;
  const pendingAmount = parseFloat(data?.outstandingYtd ?? "0") || 0;
  const hasReturnDateFilter = Boolean(
    returnDateFrom?.trim() || returnDateTo?.trim(),
  );
  const label = hasReturnDateFilter ? "Paid Sales" : "YTD Paid Sales";

  return (
    <div className="rounded-xl border bg-card overflow-hidden flex flex-col">
      <div className="p-5 pb-4">
        <div className="flex items-center gap-1.5 mb-1">
          <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <InfoHint label={`How ${label.toLowerCase()} is calculated`}>
            <p>
              {hasReturnDateFilter
                ? "Total sales from closed invoices whose trip return date falls in the selected range, paid by statement date. This is the sales value of those trips, not the amount paid."
                : "Total sales from closed invoices that paid out this calendar year, by statement date. This is the sales value of those trips, not the amount paid."}
            </p>
          </InfoHint>
        </div>
        <p className="text-4xl font-bold tracking-tight text-foreground">
          {formatKpiCurrency(paidAmount)}
        </p>
        <p className="text-lg text-muted-foreground mt-0.5">Closed invoices</p>
      </div>

      <div className="bg-muted/40 px-5 py-3.5 flex items-start justify-between gap-2 mt-auto border-t border-border/50">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-teal-700 dark:text-teal-400">
            {formatKpiCurrency(pendingAmount)} Pending
          </p>
          <p className="text-sm text-muted-foreground break-words">
            Open bookings returning this year
          </p>
        </div>
      </div>
    </div>
  );
}
